import type { JSX } from "react";

import { LeafIcon, SunIcon, TrendingArrowIcon, UserIcon, WindowIcon } from "@eisox/icons";

import type { Permission } from "~/types/auth.type";

export enum RoomDataEnum {
  autoConf = "autoConf",
  anticipation = "anticipation",
  openWin = "openWin",
  sun = "sun",
  autoPreConf = "autoPreConf",
}

export type RoomDataMap = Record<RoomDataEnum, { active: boolean; disabled: boolean }>;

export const roomDataIcon: Record<RoomDataEnum, JSX.Element> = {
  [RoomDataEnum.autoPreConf]: <LeafIcon />,
  [RoomDataEnum.autoConf]: <UserIcon />,
  [RoomDataEnum.anticipation]: <TrendingArrowIcon />,
  [RoomDataEnum.openWin]: <WindowIcon />,
  [RoomDataEnum.sun]: <SunIcon />,
} as const;

export const canViewRoomData = (permissions: Permission) =>
  permissions.valve?.stateData?.autoConf?.read &&
  permissions.valve.stateData.autoPreConf?.read &&
  permissions.valve.stateData.anticipation?.read &&
  permissions.valve.stateData.openWin?.read;

export const sunThresholds = (temperature: number, light: number) => temperature >= 26 && light >= 3000;

export interface GetValveRoomDataProps {
  autoConf?: boolean;
  anticipation?: boolean;
  openWin?: boolean;
  autoPreConf?: boolean;
  isAutoPrecomf?: boolean;
  temperature?: number;
  light?: number;
}

export const getValveRoomData = ({
  autoConf = false,
  anticipation = false,
  openWin = false,
  autoPreConf = false,
  isAutoPrecomf = false,
  temperature,
  light,
}: GetValveRoomDataProps): RoomDataMap => ({
  [RoomDataEnum.openWin]: { active: openWin, disabled: false },
  [RoomDataEnum.autoConf]: { active: autoConf, disabled: false },
  [RoomDataEnum.autoPreConf]: { active: autoPreConf, disabled: !isAutoPrecomf },
  [RoomDataEnum.anticipation]: { active: anticipation, disabled: false },
  [RoomDataEnum.sun]: {
    active: temperature !== undefined && light !== undefined && sunThresholds(temperature, light),
    disabled: false,
  },
});

interface GetRoomDataProps {
  autoConfStart?: string | null;
  anticipationStart?: string | null;
  openWinStart?: string | null;
  autoPreConfStart?: string | null;
  isAutoPrecomf?: boolean;
  valves: { temperature?: number; light?: number }[];
}

export const getRoomData = ({
  autoConfStart,
  anticipationStart,
  openWinStart,
  autoPreConfStart,
  isAutoPrecomf,
  valves,
}: GetRoomDataProps): RoomDataMap => ({
  [RoomDataEnum.openWin]: { active: openWinStart !== undefined, disabled: false },
  [RoomDataEnum.autoConf]: { active: autoConfStart !== undefined, disabled: false },
  [RoomDataEnum.autoPreConf]: { active: autoPreConfStart !== undefined, disabled: !isAutoPrecomf },
  [RoomDataEnum.anticipation]: { active: anticipationStart !== undefined, disabled: false },
  [RoomDataEnum.sun]: {
    active: valves.some(
      v => v.temperature !== undefined && v.light !== undefined && sunThresholds(v.temperature, v.light),
    ),
    disabled: false,
  },
});
